import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Skeleton,
  Alert,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI } from '../services/dashboard-api';

function descendingComparator(a, b, orderBy) {
  const aVal = a[orderBy] ?? 0;
  const bVal = b[orderBy] ?? 0;
  if (typeof aVal === 'string') return bVal.localeCompare(aVal);
  return bVal - aVal;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

const PartnerTable = ({ rows, onPartnerClick }) => {
  const [orderBy, setOrderBy] = useState('identical_matches');
  const [order, setOrder] = useState('desc');

  const handleSort = (columnId) => {
    const isDesc = orderBy === columnId && order === 'desc';
    setOrder(isDesc ? 'asc' : 'desc');
    setOrderBy(columnId);
  };

  const sorted = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={orderBy === 'company_name'}
                direction={orderBy === 'company_name' ? order : 'asc'}
                onClick={() => handleSort('company_name')}
              >
                Partner
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel
                active={orderBy === 'identical_matches'}
                direction={orderBy === 'identical_matches' ? order : 'desc'}
                onClick={() => handleSort('identical_matches')}
              >
                Identical products
              </TableSortLabel>
            </TableCell>
            <TableCell width={40} />
          </TableRow>
        </TableHead>
        <TableBody>
          {sorted.map((partner) => (
            <TableRow
              key={partner.retailer_id}
              hover
              onClick={() => onPartnerClick(partner.retailer_id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>{partner.company_name}</TableCell>
              <TableCell align="right">
                {Number(partner.identical_matches).toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ color: 'text.secondary' }}>
                <ChevronRightIcon fontSize="small" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const NetworkPage = () => {
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [groupSize, setGroupSize] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handlePartnerClick = (partnerId) => {
    navigate(`/network/${partnerId}`);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const response = await dashboardAPI.getNetworkPartners();
        if (response.success) {
          setPartners(response.data.partners || []);
          setGroupSize(response.data.group_size || 1);
        } else {
          setError('Failed to load network data');
        }
      } catch (err) {
        setError(err.message || 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Group partners by own retailer
  const grouped = useMemo(() => {
    const map = new Map();
    for (const p of partners) {
      const key = p.own_retailer_id || 'default';
      if (!map.has(key)) {
        map.set(key, { name: p.own_retailer_name || '', rows: [] });
      }
      map.get(key).rows.push(p);
    }
    return [...map.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [partners]);

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
          Network
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Identical product matches with partner retailers in the Handoff network.
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell align="right">Identical products</TableCell>
                <TableCell width={40} />
              </TableRow>
            </TableHead>
            <TableBody>
              {[...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><Skeleton width={180} /></TableCell>
                  <TableCell align="right"><Skeleton width={50} /></TableCell>
                  <TableCell><Skeleton width={20} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
        Network
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
        Identical product matches with partner retailers in the Handoff network.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {partners.length === 0 ? (
        <Typography color="textSecondary">No partner matches found.</Typography>
      ) : groupSize > 1 ? (
        grouped.map(([key, { name, rows }]) => (
          <Box key={key} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              {name}
            </Typography>
            <PartnerTable rows={rows} onPartnerClick={handlePartnerClick} />
          </Box>
        ))
      ) : (
        <PartnerTable rows={partners} onPartnerClick={handlePartnerClick} />
      )}
    </Box>
  );
};

export default NetworkPage;
